import { useEffect, useMemo, useRef, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import Gallery from "./components/gallery";

/* -------------------- Config -------------------- */
const CONFIG = {
  clientId: process.env.REACT_APP_AAD_CLIENT_ID,
  tenantId: process.env.REACT_APP_AAD_TENANT_ID || "organizations",
  redirectUri: process.env.REACT_APP_AAD_REDIRECT_URI || window.location.origin,
  functionAppScopes: (
    process.env.REACT_APP_FUNCTION_APP_AAD_SCOPE ||
    process.env.REACT_APP_AAD_SCOPE ||
    process.env.REACT_APP_AAD_GRAPH_SCOPE ||
    "User.Read"
  )
    .split(/[,\s]+/)
    .map((scope) => scope.trim())
    .filter(Boolean),
};

/* -------------------- Helpers -------------------- */
const getQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  const accessToken =
    params.get("AccessToken") ||
    params.get("access_Token") ||
    params.get("accessToken");

  return {
    accessToken,
    workid: params.get("WorkOrderID"),
    woli: params.get("WorkOrderLineItemID"),
    sa: params.get("ServiceAppointmnetID"),
    workNo: params.get("WorkOrderNo"),
    woliNo: params.get("WorkOrderLineItemNo"),
    saNo: params.get("ServiceAppointmnetNo"),
    saCaseNumber: params.get("SalesforceCaseNumber"),
    searchMetaDataKey: params.get("SearchMetaDataKey"),
    searchMetaDataValue: params.get("SearchMetaDataValue"),
    saOrderNumber: params.get("SalesforceOrderNumber"),
    saAccount: params.get("SalesforceAccount"),
    saCart: params.get("SalesforceCart"),
    siteId: params.get("SiteId"),
    siteName: params.get("SiteName"),
    patchId: params.get("PatchId"),
    patchName: params.get("PatchName"),
    recordType: params.get("RecordType"),
    updateFlag: params.get("updateFlag"),
    ptwId: params.get("PTWID"),
    ptwNumber: params.get("PTWNumber"),
  };
};

const cleanSensitiveParams = () => {
  window.history.replaceState({}, "", "/");
};

/* -------------------- Component -------------------- */
const App = () => {
  const msalRef = useRef(null);
  const authStartedRef = useRef(false);
  const initialUrlRef = useRef(window.location.href);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState("");
  const [ssoAccessToken, setSsoAccessToken] = useState("");

  const queryParams = useMemo(getQueryParams, []);
  const resolvedAccessToken = queryParams.accessToken || ssoAccessToken;

  const searchApiUrl = process.env.REACT_APP_FUNCTION_APP_SEARCH_API_URL;
  const showOnWebApiUrl = process.env.REACT_APP_FUNCTION_APP_SHOWONWEB_API_URL;

  /* -------------------- Authentication -------------------- */
  useEffect(() => {
    if (authStartedRef.current) return;
    authStartedRef.current = true;

    const authenticate = async () => {
      if (!CONFIG.clientId) {
        setAuthError("AAD client ID is missing.");
        return;
      }

      const msal = new PublicClientApplication({
        auth: {
          clientId: CONFIG.clientId,
          authority: `https://login.microsoftonline.com/${CONFIG.tenantId}`,
          redirectUri: CONFIG.redirectUri,
        },
        cache: {
          cacheLocation: "sessionStorage",
        },
      });

      msalRef.current = msal;

      try {
        await msal.initialize();

        const redirectResult = await msal.handleRedirectPromise();
        const account = redirectResult?.account || msal.getAllAccounts()[0];

        if (!account) {
          await msal.loginRedirect({ scopes: CONFIG.functionAppScopes });
          return;
        }

        const expectedTenant =
          !["organizations", "common", "consumers"].includes(
            CONFIG.tenantId.toLowerCase(),
          )
            ? CONFIG.tenantId.toLowerCase()
            : null;

        if (
          expectedTenant &&
          (account.tenantId || "").toLowerCase() !== expectedTenant
        ) {
          throw new Error(
            "Signed-in account is not part of the allowed organization.",
          );
        }

        try {
          const tokenResult = await msal.acquireTokenSilent({
            account,
            scopes: CONFIG.functionAppScopes,
          });

          setSsoAccessToken(tokenResult.accessToken);
        } catch {
          await msal.acquireTokenRedirect({
            account,
            scopes: CONFIG.functionAppScopes,
          });
          return;
        }

        setIsAuthenticated(true);
        cleanSensitiveParams();
      } catch (err) {
        setAuthError(`Sign-in failed: ${err?.message || "Unknown error"}`);
      }
    };

    authenticate();
  }, []);

  /* -------------------- UI States -------------------- */
  if (authError) {
    return (
      <div className="authGate">
        <div className="authCard">
          <h3>Sign-in Error</h3>
          <p>{authError}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="authGate">
        <div className="authCard">
          <h3>Signing you in...</h3>
          <p>Please wait while we authenticate your account.</p>
        </div>
      </div>
    );
  }

  /* -------------------- Main App -------------------- */
  return (
    <Gallery
      {...queryParams}
      accessToken={resolvedAccessToken}
      searchApiUrl={searchApiUrl}
      showOnWebApiUrl={showOnWebApiUrl}
      fullUrlWithToken={initialUrlRef.current}
    />
  );
};

export default App;
